"use client";
import { Suspense, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { ArrowRight, Plus, Search, Users, RefreshCw } from "lucide-react";
import { StaffReauth } from "../../components/desk/staff-reauth";
import { RequireRole } from "../../components/console-shell";
import { Banner, Button, Desk, Facts, Fact, PageHeader, StatusTag } from "../../components/desk";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from "../../components/ui/sheet";
import { Choice, ReadState, useRead, type PageResult } from "../../components/desk/record-tools";
import { MemberEditor, type MemberAction } from "../../components/desk/member-editor";
import {
  ATTRIBUTE_LABELS,
  FACT_KINDS,
  type Dictionary,
  type MemberDetail,
  type MemberFact,
  type MemberSummary,
  triStateLabel
} from "../../components/desk/member-model";
import { flatten, type OrgNode } from "../../components/desk/organization-model";
import { api, formatDate, formatTime, reasonText } from "../../lib/api";
import { useSession } from "../../lib/session";

// 小程序里核验页的路径，只带一次性码（重构方案 §6 E2）。
function invitePath(code: string): string {
  return `pages/binding/index?code=${encodeURIComponent(code)}`;
}

export default function MembersPage() {
  return (
    <RequireRole roleCode="PROFILE_CASE_HANDLER">
      <Suspense fallback={<Desk>正在读取会员档案…</Desk>}>
        <MemberDesk />
      </Suspense>
    </RequireRole>
  );
}
function MemberDesk() {
  const params = useSearchParams(),
    { hasRole } = useSession();
  const [word, setWord] = useState(""),
    [query, setQuery] = useState("");
  const [org, setOrg] = useState(params.get("organizationId") ?? ""),
    [status, setStatus] = useState("");
  const [cursors, setCursors] = useState<string[]>([""]),
    [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<string | null>(params.get("record")),
    [tab, setTab] = useState("basic");
  const [editor, setEditor] = useState<{
    action: MemberAction;
    detail: MemberDetail | null;
    fact?: MemberFact;
  } | null>(null);
  const [message, setMessage] = useState(""),
    [issued, setIssued] = useState<{ code: string; expiresAt: string; personId: string } | null>(
      null
    );
  const [copied, setCopied] = useState(false);
  // 身份证全号：只在重认证后读一次、只放在内存里；窗口失焦、页面隐藏、关档案、换档案都清掉。
  const [revealed, setRevealed] = useState<{ personId: string; number: string } | null>(null),
    [revealing, setRevealing] = useState(false),
    [revealError, setRevealError] = useState("");
  const focus = useRef<HTMLElement | null>(null),
    newButton = useRef<HTMLButtonElement>(null);
  const copyInvite = async (code: string) => {
    try {
      await navigator.clipboard.writeText(invitePath(code));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  const filter = new URLSearchParams({
    limit: "20",
    ...(query ? { q: query } : {}),
    ...(org ? { organizationId: org } : {}),
    ...(status ? { statusCode: status } : {}),
    ...(cursors[index] ? { cursor: cursors[index] } : {})
  });
  const list = useRead<PageResult<MemberSummary>>(`/api/v1/member-records?${filter}`);
  const tree = useRead<{ items: OrgNode[] }>("/api/v1/orgs/tree");
  const catalogs = useRead<{ items: Dictionary[] }>("/api/v1/catalog/dictionaries");
  const detail = useRead<MemberDetail>(
    selected ? `/api/v1/member-records/${encodeURIComponent(selected)}` : null
  );
  const dictionaries = Object.fromEntries(
    (catalogs.data?.items ?? []).map((d) => [d.dictCode, d.items])
  );
  const reset = () => {
    setCursors([""]);
    setIndex(0);
  };
  const open = (id: string) => {
    focus.current = document.activeElement as HTMLElement;
    setSelected(id);
    setTab("basic");
    setIssued(null);
    setRevealed(null);
    setRevealError("");
  };
  useEffect(() => {
    const hide = () => setRevealed(null);
    const onVisibility = () => { if (document.hidden) setRevealed(null); };
    window.addEventListener("blur", hide);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("blur", hide);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
  useEffect(() => { setRevealed(null); setRevealError(""); }, [selected, detail.data?.version]);
  const start = (action: MemberAction, fact?: MemberFact) => {
    setIssued(null);
    setEditor({ action, detail: action === "create" ? null : detail.data, fact });
  };
  const ready = !!tree.data && !!catalogs.data;
  // A binding code lives only while the correct member is open, and expires visibly.
  useEffect(() => {
    setCopied(false);
    if (!issued) return;
    const timer = setTimeout(
      () => setIssued(null),
      Math.max(0, Date.parse(issued.expiresAt) - Date.now())
    );
    return () => clearTimeout(timer);
  }, [issued]);
  if (editor)
    return (
      <Desk>
        <MemberEditor
          {...editor}
          defaultOrganization={org}
          organizations={tree.data?.items ?? []}
          dictionaries={dictionaries}
          onCancel={() => setEditor(null)}
          onSaved={(result) => {
            const action = editor.action;
            setEditor(null);
            setMessage(
              action === "binding"
                ? "绑定码已签发，请在有效期内交给本人。"
                : action === "identity"
                  ? "身份证号已登记，档案页只显示掩码。"
                  : "办理已完成，正在读取最新档案。"
            );
            if (action === "binding") setIssued(result as typeof issued);
            if (action === "merge") {
              setSelected(null);
              reset();
              void list.reload();
            } else if (action === "create") {
              open((result as { personId: string }).personId);
              reset();
              void list.reload();
            } else {
              void detail.reload();
              void list.reload();
            }
          }}
        />
      </Desk>
    );
  const data = detail.data;
  return (
    <Desk wide>
      <PageHeader
        eyebrow="会员与组织 / 会员档案"
        title="会员档案"
        description="按姓名与组织查找，从一份档案开始办理。"
        actions={
          <Button ref={newButton} disabled={!ready} onClick={() => start("create")}>
            <Plus size={16} />
            新建档案
          </Button>
        }
      />
      {message && <Banner tone="success">{message}</Banner>}
      {(tree.error || catalogs.error) && (
        <div className="workspace-error" role="alert">
          筛选与办理选项读取失败：{tree.error || catalogs.error}
          <Button
            variant="outline"
            onClick={() => {
              void tree.reload();
              void catalogs.reload();
            }}
          >
            重试选项
          </Button>
        </div>
      )}
      <div className="record-toolbar">
        <form
          className="record-search"
          onSubmit={(e) => {
            e.preventDefault();
            setQuery(word.trim());
            reset();
          }}
        >
          <Search size={17} />
          <input
            aria-label="搜索会员姓名"
            placeholder="搜索会员姓名"
            value={word}
            onChange={(e) => setWord(e.target.value)}
          />
          <Button variant="outline" type="submit">
            搜索
          </Button>
        </form>
        <Choice
          label="所属组织"
          empty="全部授权组织"
          value={org}
          disabled={!tree.data}
          onChange={(v) => {
            setOrg(v);
            reset();
          }}
          options={flatten(tree.data?.items ?? []).map(({ node, depth }) => ({
            value: node.id,
            label: `${"　".repeat(depth)}${node.name}`
          }))}
        />
        <Choice
          label="会籍状态"
          empty="全部状态"
          value={status}
          disabled={!catalogs.data}
          onChange={(v) => {
            setStatus(v);
            reset();
          }}
          options={(dictionaries.MEMBERSHIP_STATUS ?? []).map((s) => ({
            value: s.itemCode,
            label: s.displayName
          }))}
        />
        <Button variant="ghost" aria-label="刷新会员列表" onClick={list.reload}>
          <RefreshCw size={16} />
        </Button>
      </div>
      <ReadState {...list} retry={list.reload} />
      {!list.loading && !list.error && (
        <>
          <div className="record-list-meta">
            <span>当前页 {list.data?.items.length ?? 0} 份档案</span>
            <span>联系方式与工作信息在单条档案中查看</span>
          </div>
          {list.data?.items.length ? (
            <div className="record-table-scroll">
              <table className="record-table">
                <caption className="sr-only">会员档案列表</caption>
                <thead>
                  <tr>
                    <th>会员</th>
                    <th>所属组织</th>
                    <th>会籍状态</th>
                    <th>入会时间</th>
                    <th>界别</th>
                    <th>
                      <span className="sr-only">操作</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((row) => (
                    <tr key={row.personId}>
                      <td>
                        <button className="record-name" onClick={() => open(row.personId)}>
                          <span className="record-avatar" aria-hidden="true">
                            {row.displayName.slice(0, 1)}
                          </span>
                          <strong>{row.displayName}</strong>
                        </button>
                      </td>
                      <td>{row.organizationName}</td>
                      <td>
                        <StatusTag tone={row.statusCode === "ACTIVE" ? "success" : "neutral"}>
                          {row.statusLabel}
                        </StatusTag>
                      </td>
                      <td>
                        {row.approvedOn
                          ? formatDate(row.approvedOn)
                          : row.joinedYear
                            ? `${row.joinedYear} 年`
                            : "未登记"}
                      </td>
                      <td>{row.sectorLabel || "未登记"}</td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`查看${row.displayName}档案`}
                          onClick={() => open(row.personId)}
                        >
                          <ArrowRight size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="workspace-empty">
              <Users />
              <strong>没有匹配的档案</strong>
              <p>调整姓名、组织或会籍状态后再试。</p>
              <Button
                variant="outline"
                onClick={() => {
                  setWord("");
                  setQuery("");
                  setOrg("");
                  setStatus("");
                  reset();
                }}
              >
                清除筛选
              </Button>
            </div>
          )}
          <div className="record-pagination">
            <span>第 {index + 1} 页</span>
            <Button variant="outline" disabled={index === 0} onClick={() => setIndex(index - 1)}>
              上一页
            </Button>
            <Button
              variant="outline"
              disabled={!list.data?.meta?.nextCursor}
              onClick={() => {
                setCursors((old) => [...old.slice(0, index + 1), list.data!.meta!.nextCursor!]);
                setIndex(index + 1);
              }}
            >
              下一页
            </Button>
          </div>
        </>
      )}
      <Sheet
        open={!!selected}
        onOpenChange={(value) => {
          if (!value) {
            setSelected(null);
            setIssued(null);
            setRevealed(null);
          }
        }}
      >
        <SheetContent
          className="record-sheet workspace"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            (focus.current?.isConnected ? focus.current : newButton.current)?.focus();
          }}
        >
          <SheetHeader>
            <p className="record-eyebrow">会员档案</p>
            <SheetTitle>{data?.displayName || "读取档案"}</SheetTitle>
            <SheetDescription>
              {data?.organizationName || "按授权范围读取，查看过程留痕。"}
            </SheetDescription>
          </SheetHeader>
          <ReadState {...detail} retry={detail.reload} />
          {data && (
            <>
              {['PROFILE_CASE_HANDLER', 'PROFILE_CASE_REVIEWER'].some(hasRole) && (
                <div className="px-6 py-3"><Link className="text-sm underline underline-offset-4" href={`/cases?personId=${encodeURIComponent(data.personId)}`}>查看变更案件</Link></div>
              )}
              <nav className="record-tabs" aria-label="档案内容">
                {[
                  ["basic", "基本资料"],
                  ["relations", "组织与任职"],
                  ["facts", "履历记录"],
                  ["history", "变更记录"]
                ].map(([id, label]) => (
                  <button
                    key={id}
                    aria-current={tab === id ? "page" : undefined}
                    onClick={() => setTab(id)}
                  >
                    {label}
                  </button>
                ))}
              </nav>
              <div className="record-sheet-body">
                {issued?.personId === data.personId && (
                  <div className="record-code">
                    <strong>本次核验绑定码</strong>
                    <code>{issued.code}</code>
                    <p>有效期至 {formatTime(issued.expiresAt)}，关闭档案后不再显示。</p>
                    {/* 邀请路径只带码，不带姓名与手机号：链接会被转发，那两样进了链接就是泄露。
                        会员点开后码自动带入，姓名与手机号仍由本人填。 */}
                    <p className="record-code__invite">
                      邀请路径：<code>{invitePath(issued.code)}</code>
                    </p>
                    <p>
                      可在小程序后台据此生成小程序码或链接发给本人；会员点开后核验码自动带入，只需填姓名与手机号。
                    </p>
                    <div className="record-code__actions">
                      <Button
                        variant="outline"
                        onClick={() => void copyInvite(issued.code)}
                      >
                        {copied ? "已复制邀请路径" : "复制邀请路径"}
                      </Button>
                      <Button variant="outline" onClick={() => setIssued(null)}>
                        已交付，隐藏绑定码
                      </Button>
                    </div>
                  </div>
                )}
                {tab === "basic" && (
                  <>
                    <section className="record-section">
                      <div className="record-section-title">
                        <h2>会籍资料</h2>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!ready}
                          onClick={() => start("membership")}
                        >
                          登记会籍资料
                        </Button>
                      </div>
                      <Facts>
                        <Fact label="会籍状态">
                          <StatusTag
                            tone={data.membership.statusCode === "ACTIVE" ? "success" : "neutral"}
                          >
                            {data.membership.statusLabel}
                          </StatusTag>
                        </Fact>
                        <Fact label="批准入会日期">
                          {data.membership.approvedOn ||
                            (data.membership.joinedYear
                              ? `${data.membership.joinedYear} 年`
                              : "未登记")}
                        </Fact>
                        <Fact label="界别">{data.membership.sectorLabel || "未登记"}</Fact>
                        <Fact label="民族">{data.ethnicityLabel || "未登记"}</Fact>
                        <Fact label="政治面貌">{data.politicalStatusLabel || "未登记"}</Fact>
                        <Fact label="出生日期">
                          {data.birthDate
                            ? `${formatDate(data.birthDate)}${data.age === null ? "" : ` · ${data.age} 岁`}`
                            : "未登记"}
                        </Fact>
                        <Fact label="籍贯">{data.nativePlace || "未登记"}</Fact>
                        <Fact label="户籍所在地">{data.householdRegistration || "未登记"}</Fact>
                        <Fact label="参加工作时间">
                          {data.workStartOn ? formatDate(data.workStartOn) : "未登记"}
                        </Fact>
                        <Fact label="身份证号">
                          {!data.idDocument.recorded ? (
                            "未登记"
                          ) : revealed?.personId === data.personId ? (
                            <span className="inline-flex flex-wrap items-center gap-3">
                              <code>{revealed.number}</code>
                              <Button variant="ghost" size="sm" onClick={() => setRevealed(null)}>
                                隐藏
                              </Button>
                            </span>
                          ) : (
                            <span className="inline-flex flex-wrap items-center gap-3">
                              <code>{data.idDocument.masked ?? "已登记"}</code>
                              <Button variant="ghost" size="sm" onClick={() => { setRevealError(""); setRevealing(true); }}>
                                显示完整号码
                              </Button>
                            </span>
                          )}
                          {revealError && <p className="mt-1 text-xs text-destructive">{revealError}</p>}
                        </Fact>
                        <Fact label="地方会员编号">{data.membership.localNumber || "未登记"}</Fact>
                        <Fact label="中央会员编号">
                          {data.membership.centralNumber || "未登记"}
                        </Fact>
                        <Fact label="资料来源">{data.membership.sourceRef}</Fact>
                      </Facts>
                    </section>
                    <section className="record-section">
                      <div className="record-section-title">
                        <h2>上级报表口径</h2>
                      </div>
                      <p className="record-muted">
                        只作向上报数的口径标记，随「登记会籍资料」一并登记；不参与任何评价与排序，会员本人看不到。
                      </p>
                      <Facts>
                        <Fact label="中上层">{triStateLabel(data.membership.representativeTier)}</Fact>
                        <Fact label="企业规模">{data.membership.enterpriseScaleLabel || "未登记"}</Fact>
                        <Fact label="是否央企">{triStateLabel(data.membership.isCentralEnterprise)}</Fact>
                        <Fact label="实职安排部门类型">
                          {data.membership.postingDepartmentTypeLabel || "未登记"}
                        </Fact>
                        <Fact label="实职安排职级">{data.membership.postingRankLabel || "未登记"}</Fact>
                      </Facts>
                    </section>
                    <section className="record-section">
                      <div className="record-section-title">
                        <h2>申报资料</h2>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!ready}
                          onClick={() => start("attribute")}
                        >
                          更正申报资料
                        </Button>
                      </div>
                      <Facts>
                        {Object.entries(ATTRIBUTE_LABELS)
                          .filter(([key]) => key !== "INTERNAL_DUTY")
                          .map(([key, label]) => (
                            <Fact key={key} label={label}>
                              {data.declaredAttributes[key] || "未申报"}
                            </Fact>
                          ))}
                      </Facts>
                    </section>
                    <section className="record-section">
                      <h2>档案办理</h2>
                      <div className="workspace-actions">
                        <Button variant="outline" onClick={() => start("binding")}>
                          签发核验绑定码
                        </Button>
                        <Button variant="outline" disabled={!ready} onClick={() => start("identity")}>
                          登记身份证号
                        </Button>
                        <Button variant="ghost" onClick={() => start("merge")}>
                          合并重复档案
                        </Button>
                      </div>
                      <p className="record-muted">
                        会员本人通过姓名、手机号与一次性绑定码完成核验。
                      </p>
                    </section>
                  </>
                )}
                {tab === "relations" && (
                  <>
                    <section className="record-section">
                      <h2>当前组织关系</h2>
                      <p className="record-lead">{data.organizationName}</p>
                      {hasRole("ORGANIZATION_ADMIN") && (
                        <Link
                          className="record-link"
                          href={`/organizations?record=${encodeURIComponent(data.organizationId)}`}
                        >
                          查看组织与届次 <ArrowRight size={14} />
                        </Link>
                      )}
                    </section>
                    <section className="record-section">
                      <h2>会内任职</h2>
                      {data.appointments?.length ? (
                        data.appointments.map((a) => (
                          <article className="record-timeline-item" key={a.id}>
                            <div className="record-section-title">
                              <strong>{a.postLabel}</strong>
                              <StatusTag tone={a.isCurrent ? "brand" : "neutral"}>
                                {a.isCurrent ? "现任" : "非现任"}
                              </StatusTag>
                            </div>
                            <p>
                              {a.organizationName} · {a.termDisplayName}
                            </p>
                            <p>
                              {a.startOn || "就任日期未登记"} —{" "}
                              {a.endOn || (a.isCurrent ? "至今" : "卸任日期未登记")}
                            </p>
                            <p className="record-muted">来源：{a.sourceRef}</p>
                          </article>
                        ))
                      ) : (
                        <p className="record-muted">尚未登记任职。会内职务以组织届次记录为准。</p>
                      )}
                    </section>
                  </>
                )}
                {tab === "facts" && (
                  <>
                    <div className="record-section-title">
                      <h2>履历记录</h2>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!ready}
                        onClick={() => start("fact")}
                      >
                        登记履历
                      </Button>
                    </div>
                    <p className="record-muted">
                      最高学历：{data.derived.highestEducationLabel || "未登记"} · 工作年限：
                      {data.derived.workYears === null
                        ? "未登记"
                        : `${data.derived.workYears} 年（按当前日期推算）`}
                    </p>
                    {FACT_KINDS.map((kind) => (
                      <section className="record-section" key={kind.kind}>
                        <h3>{kind.label}</h3>
                        {data.facts
                          .filter((f) => f.kind === kind.kind)
                          .map((f) => (
                            <article className="record-timeline-item" key={f.id}>
                              <div className="record-section-title">
                                <strong>{f.title}</strong>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => start("supersede", f)}
                                >
                                  收档
                                </Button>
                              </div>
                              <p>
                                {[f.subtitle, f.primaryLabel, f.secondaryLabel]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                              <p>
                                {f.startOn || "开始日期未登记"} — {f.endOn || "结束日期未登记"}
                              </p>
                              {f.note && <p>{f.note}</p>}
                              <p className="record-muted">来源：{f.sourceRef}</p>
                            </article>
                          ))}
                        {!data.facts.some((f) => f.kind === kind.kind) && (
                          <p className="record-muted">尚未登记</p>
                        )}
                      </section>
                    ))}
                  </>
                )}
                {tab === "history" && (
                  <MemberHistory key={data.personId} personId={data.personId} />
                )}
              </div>
            </>
          )}
          {revealing && data && (
            <StaffReauth
              title="显示身份证号"
              description="每次显示都会记入办理留痕；号码只在本页显示，离开页面或窗口失焦即清除。"
              onComplete={async () => {
                try {
                  const result = await api<{ personId: string; idDocumentNumber: string | null }>(
                    `/api/v1/member-records/${encodeURIComponent(data.personId)}/identity-document`
                  );
                  setRevealed(result.idDocumentNumber ? { personId: result.personId, number: result.idDocumentNumber } : null);
                  if (!result.idDocumentNumber) setRevealError("这份档案没有登记身份证号。");
                } catch (error) {
                  setRevealError(reasonText(error));
                }
              }}
              onClose={() => setRevealing(false)}
            />
          )}
        </SheetContent>
      </Sheet>
    </Desk>
  );
}
const ACTIONS: Record<string, string> = {
  MEMBER_RECORD_CREATED: "建立档案",
  MEMBER_RECORD_UPDATED: "登记会籍资料",
  MEMBER_ATTRIBUTE_CORRECTED: "更正申报资料",
  MEMBER_FACT_RECORDED: "登记履历",
  MEMBER_FACT_SUPERSEDED: "收档履历",
  MEMBER_RECORD_MERGED: "合并重复档案",
  MEMBER_BINDING_CODE_ISSUED: "签发核验绑定码",
  MEMBER_ID_DOCUMENT_RECORDED: "登记身份证号",
  MEMBER_ID_DOCUMENT_REVEALED: "查看身份证号"
};
function MemberHistory({ personId }: { personId: string }) {
  const [cursor, setCursor] = useState("");
  const read = useRead<
    PageResult<{
      id: string;
      occurredAt: string;
      action: string;
      sourceRef: string | null;
      reason: string | null;
    }>
  >(
    `/api/v1/member-records/${encodeURIComponent(personId)}/history?${new URLSearchParams({ limit: "20", ...(cursor ? { cursor } : {}) })}`
  );
  return (
    <section className="record-section">
      <h2>办理与变更记录</h2>
      <p className="record-muted">
        展示已留存的建档、会籍更正、履历与核验码办理依据；任职沿革见“组织与任职”。
      </p>
      <ReadState {...read} retry={read.reload} />
      {read.data?.items.length === 0 && <p>尚无可展示的办理记录。</p>}
      {read.data?.items.map((event) => (
        <article className="record-timeline-item" key={event.id}>
          <time>{formatTime(event.occurredAt)}</time>
          <h3>{ACTIONS[event.action] || event.action}</h3>
          <p>{event.reason || "未附办理理由"}</p>
          <p className="record-muted">来源：{event.sourceRef || "未附来源"}</p>
        </article>
      ))}
      <div className="workspace-actions">
        {cursor && (
          <Button variant="outline" onClick={() => setCursor("")}>
            返回最新
          </Button>
        )}
        {read.data?.meta?.nextCursor && (
          <Button variant="outline" onClick={() => setCursor(read.data!.meta!.nextCursor!)}>
            更早记录
          </Button>
        )}
      </div>
    </section>
  );
}
